import React, { useCallback, useEffect, useMemo, useState } from 'react';
import toast from 'react-hot-toast';
import { HiOutlineRefresh, HiOutlineSave, HiOutlineUpload } from 'react-icons/hi';
import { Link } from 'react-router-dom';
import { settingsApi } from '../../services/api';
import { formatCalendarDateDMY } from '../../utils/timezoneDisplay';

const JULY_2026_TOTALS_BY_NAME = {
  manisha: 11,
  dhruvi: 9,
  bhoomi: 7,
  jasmin: 5,
  dristi: 9.5,
  trupati: 5,
  snehal: 5,
  'dr avani': 5,
  avani: 5,
  rani: 7,
  nandani: 7,
};

function roundHalf(n) {
  return Math.round((Number(n) || 0) * 2) / 2;
}

function normalizeName(name) {
  return String(name || '')
    .toLowerCase()
    .replace(/\./g, '')
    .replace(/\s+/g, ' ')
    .trim();
}

function computeOpeningFromTarget(targetJuneCarryOut, juneMonthlyCredit, juneUsedDays) {
  return roundHalf(roundHalf(targetJuneCarryOut) - roundHalf(juneMonthlyCredit) + roundHalf(juneUsedDays));
}

function suggestTargetFromTotal(total, exitAccrual) {
  return roundHalf(Math.max(0, roundHalf(total) - roundHalf(exitAccrual)));
}

function matchJulyTotal(fullName) {
  const key = normalizeName(fullName);
  if (JULY_2026_TOTALS_BY_NAME[key] != null) return JULY_2026_TOTALS_BY_NAME[key];
  for (const [name, total] of Object.entries(JULY_2026_TOTALS_BY_NAME)) {
    if (key.includes(name) || name.includes(key)) return total;
  }
  return null;
}

const StaffLeaveOpeningSetup = () => {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [meta, setMeta] = useState(null);
  const [rows, setRows] = useState([]);
  const [previewYear] = useState(2026);
  const [previewMonth] = useState(7);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await settingsApi.getStaffLeaveOpening({ year: previewYear, month: previewMonth });
      const data = res.data.data || {};
      setMeta(data);
      const mapped = (data.staff || []).map((row) => {
        const sheetTotal = matchJulyTotal(row.full_name);
        const suggestedTarget =
          sheetTotal != null
            ? suggestTargetFromTotal(sheetTotal, row.exit_leave_accrual)
            : row.target_june_carry_out ?? row.june_carry_forward_out;
        return {
          ...row,
          target_june_carry_out: suggestedTarget,
        };
      });
      setRows(mapped);
    } catch (e) {
      toast.error(e.response?.data?.message || 'Failed to load staff leave opening data');
      setRows([]);
    } finally {
      setLoading(false);
    }
  }, [previewYear, previewMonth]);

  useEffect(() => {
    load();
  }, [load]);

  const enrichedRows = useMemo(
    () =>
      rows.map((row) => {
        const target = roundHalf(row.target_june_carry_out);
        const computed = computeOpeningFromTarget(target, row.june_monthly_credit, row.june_used_days);
        const changed = computed !== roundHalf(row.opening_leave_balance);
        return { ...row, computed_opening_balance: computed, opening_changed: changed };
      }),
    [rows]
  );

  const changedCount = enrichedRows.filter((r) => r.opening_changed).length;

  const updateTarget = (userId, value) => {
    setRows((prev) =>
      prev.map((row) =>
        Number(row.user_id) === Number(userId)
          ? { ...row, target_june_carry_out: value === '' ? '' : Number(value) }
          : row
      )
    );
  };

  const applySheetSuggestions = () => {
    setRows((prev) =>
      prev.map((row) => {
        const sheetTotal = matchJulyTotal(row.full_name);
        if (sheetTotal == null) return row;
        return {
          ...row,
          target_june_carry_out: suggestTargetFromTotal(sheetTotal, row.exit_leave_accrual),
        };
      })
    );
    toast.success('Applied July sheet targets (total minus exit accrual)');
  };

  const handleCsvImport = (event) => {
    const file = event.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = () => {
      try {
        const text = String(reader.result || '');
        const lines = text.split(/\r?\n/).filter((line) => line.trim());
        const byName = new Map();
        for (const line of lines.slice(1)) {
          const [namePart, valuePart] = line.split(',');
          const name = normalizeName(namePart);
          const value = parseFloat(String(valuePart || '').trim());
          if (name && Number.isFinite(value)) byName.set(name, value);
        }
        setRows((prev) =>
          prev.map((row) => {
            const key = normalizeName(row.full_name);
            let target = byName.get(key);
            if (target == null) {
              for (const [csvName, val] of byName.entries()) {
                if (key.includes(csvName) || csvName.includes(key)) {
                  target = val;
                  break;
                }
              }
            }
            if (target == null) return row;
            return { ...row, target_june_carry_out: roundHalf(target) };
          })
        );
        toast.success('CSV targets imported');
      } catch {
        toast.error('Could not parse CSV');
      } finally {
        event.target.value = '';
      }
    };
    reader.readAsText(file);
  };

  const handleApply = async () => {
    const updates = enrichedRows
      .filter((row) => row.opening_changed)
      .map((row) => ({
        user_id: row.user_id,
        target_june_carry_out: roundHalf(row.target_june_carry_out),
      }));
    if (updates.length === 0) {
      toast('No opening balance changes to apply');
      return;
    }
    setSaving(true);
    try {
      const res = await settingsApi.bulkUpdateStaffLeaveOpening({ updates });
      toast.success(res.data.message || 'Opening balances updated');
      await load();
    } catch (e) {
      toast.error(e.response?.data?.message || 'Failed to apply opening balances');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return <div className="p-6 text-sm text-gray-500">Loading staff leave opening setup…</div>;
  }

  return (
    <div className="mx-auto w-full max-w-[1400px] space-y-6 pb-8 animate-fade-in">
      <div>
        <h1 className="font-display text-2xl font-bold text-gray-900">Staff leave opening setup</h1>
        <p className="mt-1 max-w-3xl text-sm text-gray-500">
          Set <strong>June carry-forward</strong> (monthly pool into July, before July credit) using{' '}
          <strong>Target June out</strong> per staff. Exit accrual is read from each staff profile (join
          date + probation). June leave used comes from attendance already marked — do not re-enter June
          attendance here.
        </p>
        {meta && (
          <p className="mt-2 text-xs text-gray-500">
            Ledger cutover: <strong>{meta.ledger_cutover_month}</strong> · Preview month:{' '}
            <strong>{meta.july_month}</strong> · Default June credit:{' '}
            <strong>{meta.june_monthly_credit}</strong> days
          </p>
        )}
      </div>

      <div className="card flex flex-col gap-3 sm:flex-row sm:flex-wrap sm:items-center sm:justify-between">
        <p className="text-sm text-gray-600">
          Formula:{' '}
          <code className="rounded bg-gray-100 px-1.5 py-0.5 text-xs">
            opening = target_june_carry − june_credit + june_used
          </code>
        </p>
        <div className="flex flex-wrap gap-2">
          <button type="button" onClick={applySheetSuggestions} className="btn btn-secondary text-sm">
            Apply July sheet targets
          </button>
          <label className="btn btn-secondary text-sm cursor-pointer">
            <HiOutlineUpload className="mr-1.5 inline h-4 w-4" />
            Import CSV
            <input type="file" accept=".csv,text/csv" className="hidden" onChange={handleCsvImport} />
          </label>
          <button type="button" onClick={load} className="btn btn-secondary text-sm">
            <HiOutlineRefresh className="mr-1.5 inline h-4 w-4" />
            Refresh
          </button>
          <button
            type="button"
            onClick={handleApply}
            disabled={saving || changedCount === 0}
            className="btn btn-primary text-sm disabled:opacity-50"
          >
            <HiOutlineSave className="mr-1.5 inline h-4 w-4" />
            {saving ? 'Applying…' : `Apply & rebuild (${changedCount})`}
          </button>
        </div>
      </div>

      <div className="overflow-x-auto rounded-xl border border-gray-200 bg-white shadow-sm">
        <table className="min-w-full divide-y divide-gray-200 text-sm">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-3 py-2 text-left font-semibold text-gray-700">Staff</th>
              <th className="px-3 py-2 text-left font-semibold text-gray-700">Join date</th>
              <th className="px-3 py-2 text-right font-semibold text-gray-700">Exit</th>
              <th className="px-3 py-2 text-right font-semibold text-gray-700">June used</th>
              <th className="px-3 py-2 text-right font-semibold text-gray-700">June credit</th>
              <th className="px-3 py-2 text-right font-semibold text-gray-700">Current June out</th>
              <th className="px-3 py-2 text-right font-semibold text-gray-700">Target June out</th>
              <th className="px-3 py-2 text-right font-semibold text-gray-700">Current opening</th>
              <th className="px-3 py-2 text-right font-semibold text-gray-700">New opening</th>
              <th className="px-3 py-2 text-right font-semibold text-gray-700">July avail</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100">
            {enrichedRows.length === 0 ? (
              <tr>
                <td colSpan={10} className="px-4 py-8 text-center text-gray-500">
                  No active staff found.
                </td>
              </tr>
            ) : (
              enrichedRows.map((row) => (
                <tr key={row.user_id} className={row.opening_changed ? 'bg-amber-50/60' : ''}>
                  <td className="px-3 py-2 font-medium text-gray-900">
                    <Link to={`/staff`} className="hover:text-primary-700 hover:underline">
                      {row.full_name}
                    </Link>
                  </td>
                  <td className="px-3 py-2 text-gray-600">
                    {row.join_date ? formatCalendarDateDMY(String(row.join_date).slice(0, 10)) : '—'}
                  </td>
                  <td className="px-3 py-2 text-right tabular-nums text-gray-700">
                    {row.exit_leave_accrual.toFixed(1)}
                  </td>
                  <td className="px-3 py-2 text-right tabular-nums text-gray-700">
                    {row.june_used_days.toFixed(1)}
                    {row.june_snapshot_used_days != null &&
                    row.june_snapshot_used_days !== row.june_used_days ? (
                      <span
                        className="ml-1 text-xs text-amber-600"
                        title={`Ledger snapshot still shows ${row.june_snapshot_used_days.toFixed(1)} — run Apply & rebuild to sync`}
                      >
                        *
                      </span>
                    ) : null}
                  </td>
                  <td className="px-3 py-2 text-right tabular-nums text-gray-700">
                    {row.june_monthly_credit.toFixed(1)}
                  </td>
                  <td className="px-3 py-2 text-right tabular-nums text-gray-700">
                    {row.june_carry_forward_out.toFixed(1)}
                  </td>
                  <td className="px-3 py-2 text-right">
                    <input
                      type="number"
                      min="0"
                      step="0.5"
                      value={row.target_june_carry_out}
                      onChange={(e) => updateTarget(row.user_id, e.target.value)}
                      className="w-20 rounded border border-gray-300 px-2 py-1 text-right text-sm tabular-nums focus:ring-2 focus:ring-primary-500"
                    />
                  </td>
                  <td className="px-3 py-2 text-right tabular-nums text-gray-600">
                    {row.opening_leave_balance.toFixed(1)}
                  </td>
                  <td
                    className={`px-3 py-2 text-right tabular-nums font-semibold ${
                      row.opening_changed ? 'text-amber-800' : 'text-gray-700'
                    }`}
                  >
                    {row.computed_opening_balance.toFixed(1)}
                  </td>
                  <td className="px-3 py-2 text-right tabular-nums text-primary-700">
                    {row.july_avail_preview.toFixed(1)}
                    {row.in_probation ? (
                      <span className="ml-1 text-xs text-amber-700">(probation)</span>
                    ) : null}
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      <div className="rounded-lg border border-blue-200 bg-blue-50 p-4 text-sm text-blue-900">
        <p className="font-semibold">Verify after apply</p>
        <ul className="mt-2 list-inside list-disc space-y-1 text-blue-800">
          <li>
            Staff → Attendance → Summary → July {previewYear}: check <strong>avail</strong> and{' '}
            <strong>exit</strong> per person
          </li>
          <li>June register should stay unchanged — only opening balance on the profile is updated</li>
          <li>
            Wrong exit? Edit staff <strong>Join date</strong> on Staff page, then run Apply again or use{' '}
            Configuration → Attendance automation → Rebuild
          </li>
        </ul>
      </div>
    </div>
  );
};

export default StaffLeaveOpeningSetup;
